/* eslint-disable max-lines -- product certificate mapping utilities share row/option transforms */
import type { ComboBoxOption } from 'components/inputs/types';
import type {
  CertificateWarningFor,
  FacilityDetail,
  PoProcessType,
  ProductCertificateDetail,
  StyleData,
  StyleProductCertificateOption,
  SubsupplierDetail,
} from 'features/purchase-orders/types';
import { hasChangesDisabled } from 'features/purchase-orders/utils';
import type { FacilityCertificate } from 'features/suppliers/types';
import type {
  PoFacilityCertificatePayload,
  PoFacilityProcessCertificateResponse,
} from 'features/purchase-orders/api/useUpdateProductCertificates';
import { ProductCertificateFilterOptionElement } from './config-filter';
import { styledTheme } from 'styles/theme/theme';
import type { BaseColumns } from 'types/table';

const PRODUCT_CERTIFICATE_GROUP_HEADER_PREFIX = '__product-cert-group__';

export type CertificateInfoExtract = {
  id: string;
  label: string;
  supplierName: string;
  supplierAvatarColor: string;
  supplierId?: string;
  customProps?: ComboBoxOption['customProps'];
};

const dedupeBy = <T>(items: T[], key: (item: T) => string) => [
  ...new Map(items.map(item => [key(item), item])).values(),
];

const sortByLabel = <T extends { label: string }>(items: T[]) =>
  [...items].sort((a, b) => a.label.localeCompare(b.label));

export const getProductCertificateDisplayName = (
  cert: Pick<Partial<CertificateInfoExtract>, 'label' | 'customProps'>
): string => {
  const standardName = String(cert.customProps?.standardName ?? '').trim();
  if (standardName) return standardName;
  const [nameOnly] = String(cert.label ?? '').split(' · ');
  return nameOnly?.trim() || String(cert.label ?? '').trim();
};

const PRODUCT_CERTIFICATE_STATUS_COLOR: Record<string, string> = {
  expired: styledTheme.palette.fg.errorPrimary,
  expiring_soon: styledTheme.palette.fg.warningPrimary,
  not_yet_valid: styledTheme.palette.fg.infoPrimary,
  valid: styledTheme.palette.fg.successPrimary,
  inactive: styledTheme.palette.fg.secondary,
};

export const getProductCertificateStatusColor = (cert: Partial<CertificateInfoExtract>): string => {
  const status = String(cert.customProps?.status ?? '').toLowerCase();
  if (!status) {
    return styledTheme.palette.fg.successPrimary;
  }

  return PRODUCT_CERTIFICATE_STATUS_COLOR[status] ?? styledTheme.palette.fg.successPrimary;
};

export const mapComboBoxToCertificateInfo = (
  items: Partial<ComboBoxOption>[]
): Partial<CertificateInfoExtract>[] =>
  items.map(({ id, label, customProps }) => ({
    id: String(id),
    label: String(label ?? ''),
    supplierName: String(customProps?.supplierName ?? ''),
    supplierAvatarColor: String(customProps?.supplierAvatarColor ?? ''),
    supplierId: typeof customProps?.supplierId === 'string' ? customProps.supplierId : undefined,
    customProps,
  }));

export const mapCertificateInfoToComboBox = (
  items: Partial<CertificateInfoExtract>[]
): Partial<ComboBoxOption>[] =>
  items.map(({ id, label, customProps }) => ({ id, label, customProps }));

export const findStyleRowInMapList = (rows: StyleData[] | undefined, styleId: string) =>
  rows?.find(row => String(row.styleId ?? row.id) === styleId);

export const findVariantStyleRowInMapList = (
  rows: StyleData[] | undefined,
  styleId: string,
  poVariantId: string | number,
  styleColorId?: string | number
): StyleData | undefined => {
  const parent = findStyleRowInMapList(rows, styleId);
  if (!parent) return undefined;

  const matchesVariant = (row: StyleData) =>
    String(row.poVariantId) === String(poVariantId) &&
    (styleColorId == null ||
      styleColorId === 'undefined' ||
      String(row.styleColorId) === String(styleColorId));

  return parent.childs?.find(matchesVariant) ?? (matchesVariant(parent) ? parent : undefined);
};
export const hasNeedsReviewMappedFacility = (row: StyleData, processTypeId: number): boolean =>
  (getStyleProcessDetailsForRow(row, processTypeId)?.subsupplierDetails ?? []).some(
    sub =>
      sub.status === 'NeedsReview' ||
      (sub.facilityDetails ?? []).some(facility => facility.status === 'NeedsReview')
  );

const mergeCertificateWarning = (
  current: CertificateWarningFor | undefined,
  next?: CertificateWarningFor
): CertificateWarningFor | undefined => {
  if (next === 'expired') return 'expired';
  if (next === 'expiring_soon' && current !== 'expired') return 'expiring_soon';
  return current;
};

const getCertificateWarningFromProcessTypes = (processTypes?: PoProcessType[]) =>
  processTypes?.reduce<CertificateWarningFor | undefined>(
    (warning, process) =>
      process.subsupplierDetails?.reduce(
        (subWarning, sub) => mergeCertificateWarning(subWarning, sub.certificateWarningFor),
        warning
      ) ?? warning,
    undefined
  );

const walkCertificateWarnings = (styles: StyleData[]): CertificateWarningFor | undefined =>
  styles.reduce<CertificateWarningFor | undefined>((warning, style) => {
    let next = mergeCertificateWarning(
      warning,
      getCertificateWarningFromProcessTypes(style.productCertificateDetailsList)
    );
    next = mergeCertificateWarning(
      next,
      getCertificateWarningFromProcessTypes(style.subsupplierFacilityDetailsList)
    );
    return style.childs?.length
      ? mergeCertificateWarning(next, walkCertificateWarnings(style.childs))
      : next;
  }, undefined);

export const getCertificateWarningForStyles = (styles: StyleData[] | undefined) =>
  styles?.length ? walkCertificateWarnings(styles) : undefined;

export const getStyleMapSuppliersRowId = (
  row: Pick<StyleData, 'poVariantId' | 'styleColorId' | 'styleId'>
): string => `${row.poVariantId ?? ''}-${row.styleColorId ?? ''}-${row.styleId ?? ''}`;

export const isMultiVariantStyleParentRow = (row: StyleData): boolean =>
  (row.childs?.length ?? 0) > 1;

/** Same row/process resolution as map-styles facility dropdown (`formatPoProcessTypesToTableHeaderForStyles`). */
export const getStyleProcessDetailsForCertificateDisplay = (
  row: StyleData,
  processTypeId: number,
  listKey:
    | 'subsupplierFacilityDetailsList'
    | 'productCertificateDetailsList' = 'subsupplierFacilityDetailsList'
): PoProcessType | undefined => {
  const processDetail = row?.[listKey]?.find(
    subsupplierFacility => subsupplierFacility.id === processTypeId
  );
  if (!processDetail) return undefined;

  if (isMultiVariantStyleParentRow(row)) {
    return processDetail;
  }

  return {
    ...processDetail,
    subsupplierDetails:
      processDetail.subsupplierDetails?.filter(
        subsupplierDetail => subsupplierDetail?.poVariantId === row?.poVariantId
      ) ?? [],
  };
};

export const getStyleProcessDetailsForRow = getStyleProcessDetailsForCertificateDisplay;

const getMappedFacilitiesForRow = (row: StyleData, processTypeId: number) =>
  getStyleProcessDetailsForRow(
    row,
    processTypeId,
    'subsupplierFacilityDetailsList'
  )?.subsupplierDetails?.flatMap(sub => sub.facilityDetails ?? []) ?? [];

export const getPoFacilityProcessIdForRow = (
  row: StyleData,
  processTypeId: number,
  mappedFacilityId?: string
) => {
  const facilities = getMappedFacilitiesForRow(row, processTypeId);
  const facility = mappedFacilityId
    ? facilities.find(item => String(item.id) === mappedFacilityId)
    : facilities.find(item => item.poFacilityProcessId != null);
  return facility?.poFacilityProcessId ?? undefined;
};

const PRODUCT_CERTIFICATE_ASSIGNMENT_LIST_KEYS = [
  'productCertificateDetailsList',
  'subsupplierFacilityDetailsList',
] as const;

type AssignedCertificateCatalogContext = {
  poSubSupplierProcessTypes?: PoProcessType[];
  processTypeId?: number;
};

/** Same physical cert across variants may have different assignment ids — dedupe on catalog or facility + standard + number. */
export const getCertificateDisplayDedupeKey = (
  cert: Pick<CertificateInfoExtract, 'id' | 'customProps'>
): string => {
  const facilityId = String(cert.customProps?.facilityId ?? '').trim();
  const facilityCertificateId = Number(cert.customProps?.facilityCertificateId);
  if (Number.isFinite(facilityCertificateId) && facilityCertificateId > 0) {
    return `fc::${facilityCertificateId}`;
  }

  const standardName = String(cert.customProps?.standardName ?? '')
    .trim()
    .toLowerCase();
  const certificateNumber = String(cert.customProps?.certificateNumber ?? '')
    .trim()
    .toLowerCase();
  if (facilityId && (standardName || certificateNumber)) {
    return `${facilityId}::${standardName}::${certificateNumber}`;
  }

  return String(cert.id);
};

export const getSelectedCertificatesForDisplay = (
  row: StyleData,
  processTypeId: number,
  poSubSupplierProcessTypes?: PoProcessType[]
): CertificateInfoExtract[] => {
  const context: AssignedCertificateCatalogContext = {
    poSubSupplierProcessTypes,
    processTypeId,
  };
  const certificates = dedupeBy(
    PRODUCT_CERTIFICATE_ASSIGNMENT_LIST_KEYS.flatMap(listKey =>
      extractCertificateInfo(
        getStyleProcessDetailsForCertificateDisplay(row, processTypeId, listKey),
        context
      )
    ),
    cert => cert.id
  );

  if (!isMultiVariantStyleParentRow(row)) {
    return certificates;
  }

  return dedupeBy(certificates, getCertificateDisplayDedupeKey);
};

/** Same dedupe key as parent dropdown tags — used to find each variant's assignment on remove. */
export const getProductCertificateDetailDedupeKey = (cert: ProductCertificateDetail) =>
  getCertificateDisplayDedupeKey({
    id: cert.id,
    customProps: {
      facilityId: cert.facilityId,
      facilityCertificateId:
        Number.isFinite(Number(cert.id)) && !String(cert.id).startsWith('orphan-')
          ? Number(cert.id)
          : undefined,
      standardName: cert.standardName,
      certificateNumber: cert.certificateNumber,
    },
  });

export const getAssignedProductCertificatesForStyleProcess = (
  row: StyleData,
  processTypeId: number,
  poSubSupplierProcessTypes?: PoProcessType[]
): ProductCertificateDetail[] => {
  const context: AssignedCertificateCatalogContext = {
    poSubSupplierProcessTypes,
    processTypeId,
  };
  return dedupeBy(
    PRODUCT_CERTIFICATE_ASSIGNMENT_LIST_KEYS.flatMap(listKey =>
      getAssignedProductCertificatesForProcess(
        getStyleProcessDetailsForRow(row, processTypeId, listKey),
        context
      )
    ),
    cert => cert.id
  );
};

export const isSelectedCertificateRemovable = (item: Partial<ComboBoxOption>): boolean =>
  !hasChangesDisabled(item.customProps?.uiCapabilities);

export const isProductCertificateChangesDisabled = (item: Partial<ComboBoxOption> = {}): boolean =>
  hasChangesDisabled(item.customProps?.uiCapabilities);

export const isProductCertificatePreviewDisabled = (
  item: Partial<ComboBoxOption> = {},
  isSupplier = false
): boolean => isSupplier && isProductCertificateChangesDisabled(item);

/** Append assigned certs missing from catalog options so they appear checked in the dropdown. */
export const mergeSelectedCertificatesIntoDropdownOptions = (
  catalogOptions: ComboBoxOption[],
  selectedValues: Partial<ComboBoxOption>[]
): ComboBoxOption[] => {
  const existingIds = new Set(catalogOptions.map(option => String(option.id)));
  const extraOptions = selectedValues
    .filter(item => !existingIds.has(String(item.id)))
    .map(item => {
      const displayName = getProductCertificateDisplayName(item);
      const certificateNumber = String(item.customProps?.certificateNumber ?? '').trim();
      const facilityName = String(
        item.customProps?.facilityName ?? item.customProps?.supplierName ?? ''
      ).trim();

      return {
        id: String(item.id),
        label: String(item.label ?? displayName),
        customProps: {
          ...item.customProps,
          label: displayName,
          searchText: [facilityName, displayName, certificateNumber].filter(Boolean).join(' '),
        },
      };
    });

  return extraOptions.length > 0 ? [...catalogOptions, ...extraOptions] : catalogOptions;
};

export const isProductCertificateNonSelectableOption = (
  option: ComboBoxOption | undefined,
  selectedValues: Partial<ComboBoxOption>[]
): boolean => {
  if (!option) {
    return false;
  }
  if (isProductCertificateGroupHeaderOption(option)) {
    return true;
  }

  const selected = selectedValues.find(item => String(item.id) === String(option.id));
  return Boolean(selected && !isSelectedCertificateRemovable(selected));
};

export const hasMappedSupplierForStyleProcess = (row: StyleData, processTypeId: number): boolean =>
  isMultiVariantStyleParentRow(row)
    ? row.childs?.some(child => hasMappedSupplierForStyleProcess(child, processTypeId)) ?? false
    : getStyleProcessDetailsForRow(row, processTypeId)?.subsupplierDetails?.some(
        sub => (sub.facilityDetails?.length ?? 0) > 0
      ) ?? false;

/** Red border when a selected cert sits on a mapped facility that needs review. */
export const hasNeedsReviewSelectedCertificate = (
  selectedCertificates: Pick<CertificateInfoExtract, 'customProps'>[],
  row: StyleData,
  processTypeId: number
): boolean => {
  if (!selectedCertificates.length) {
    return false;
  }

  const needsReviewFacilityIds = new Set(
    (getStyleProcessDetailsForRow(row, processTypeId)?.subsupplierDetails ?? [])
      .flatMap(sub => sub.facilityDetails ?? [])
      .filter(facility => facility.status === 'NeedsReview')
      .map(facility => String(facility.id))
  );

  return selectedCertificates.some(cert => {
    const facilityId = String(cert.customProps?.facilityId ?? '');
    if (facilityId && needsReviewFacilityIds.has(facilityId)) {
      return true;
    }
    return cert.customProps?.supplierStatus === 'NeedsReview';
  });
};

export const productCertificateErrorTriggerSx = {
  borderColor: styledTheme.palette.border.error,
  boxShadow: 'none',
  '&:hover': {
    borderColor: styledTheme.palette.border.errorSolid,
  },
} as const;

type SubSupplierFacilityCertificate = FacilityCertificate & { certificateType?: number | string };

/** Product cert when certificateType or standard.certificateType is explicitly product. */
export const isProductCertificate = (certificate: SubSupplierFacilityCertificate) => {
  const certType = certificate.certificateType;
  const standardType = certificate.standard?.certificateType?.toLowerCase();

  return certType === 1 || certType === 'product' || standardType === 'product';
};

export const isProductCertificateGroupHeaderOption = (option?: ComboBoxOption) =>
  option != null &&
  (Boolean(option.customProps?.isGroupHeader) ||
    String(option.id).startsWith(PRODUCT_CERTIFICATE_GROUP_HEADER_PREFIX));

const payloadKey = (cert: PoFacilityCertificatePayload) =>
  'poFacilityCertificateId' in cert
    ? `remove:${cert.poFacilityCertificateId}`
    : `assign:${cert.poFacilityProcessId}:${cert.facilityCertificateId}`;

const dedupePoFacilityCertificatePayloads = (certificates: PoFacilityCertificatePayload[]) =>
  dedupeBy(certificates, payloadKey);

export const getStyleMapSupplierRowKeysFromItem = (item: StyleData): string[] =>
  isMultiVariantStyleParentRow(item)
    ? item.childs?.map(getStyleMapSuppliersRowId) ?? []
    : [getStyleMapSuppliersRowId(item)];

export const resolveStyleMapSupplierRowKeysForUpdate = (
  item: StyleData | undefined,
  selectedRowIds: string[] = []
): string[] => {
  if (selectedRowIds.length > 1) return selectedRowIds;

  const fromItem = item ? getStyleMapSupplierRowKeysFromItem(item) : [];
  const rows = selectedRowIds.length ? selectedRowIds : fromItem;

  if (
    item &&
    isMultiVariantStyleParentRow(item) &&
    rows.length === 1 &&
    rows[0] === getStyleMapSuppliersRowId(item)
  ) {
    return fromItem;
  }

  return rows;
};

export const buildPoFacilityCertificatePayloads = (
  rowIds: string[],
  getPoFacilityProcessId: (
    poVariantId: string,
    styleId: string,
    styleColorId: string
  ) => number | undefined,
  buildPayloadForRow: (
    poVariantId: string,
    styleId: string,
    styleColorId: string,
    poFacilityProcessId: number
  ) => PoFacilityCertificatePayload | null
): PoFacilityCertificatePayload[] => {
  const payloads: PoFacilityCertificatePayload[] = [];

  for (const rowId of rowIds) {
    const [poVariantId, styleColorId, styleId] = rowId.split('-');
    if (!styleColorId || styleColorId === 'undefined') continue;

    const poFacilityProcessId = getPoFacilityProcessId(poVariantId, styleId, styleColorId);
    if (poFacilityProcessId == null) continue;

    const payload = buildPayloadForRow(poVariantId, styleId, styleColorId, poFacilityProcessId);
    if (payload != null) payloads.push(payload);
  }

  return dedupePoFacilityCertificatePayloads(payloads);
};

export const toPoFacilityCertificatesRequest = (
  certificates: PoFacilityCertificatePayload[],
  designerOrganizationId?: number
) => ({
  designerOrganizationId,
  certificates,
});

/** @deprecated Use buildPoFacilityCertificatePayloads */
export const buildCertificateProcessUpdates = buildPoFacilityCertificatePayloads;

/** @deprecated Use toPoFacilityCertificatesRequest */
export const toFacilityProcessCertificatesRequest = (
  certificates: PoFacilityCertificatePayload[],
  designerOrganizationId?: number
) => toPoFacilityCertificatesRequest(certificates, designerOrganizationId);

const isPoFacilityProcessCertificateResponseArray = (
  value: unknown
): value is PoFacilityProcessCertificateResponse[] => Array.isArray(value);

export const extractPoFacilityCertificateResponses = (
  response: unknown
): PoFacilityProcessCertificateResponse[] => {
  if (!response || typeof response !== 'object' || !('data' in response)) return [];
  const { data } = response;
  return isPoFacilityProcessCertificateResponseArray(data) ? data : [];
};

export const mapPoFacilityCertificateResponseToDetail = (
  certificate: PoFacilityProcessCertificateResponse
): ProductCertificateDetail => {
  const standardName = certificate.certificateStandard?.trim() ?? '';
  const certificateNumber = certificate.certificateNumber?.trim() ?? '';
  const label =
    standardName && certificateNumber
      ? `${standardName} · ${certificateNumber}`
      : standardName || certificateNumber;

  return {
    id: String(certificate.facilityCertificateId),
    name: label,
    poStyleProductCertificateId: certificate.id,
    certificateNumber: certificate.certificateNumber,
    standardName: certificate.certificateStandard,
    validFrom: certificate.validFrom,
    validTo: certificate.validTo,
  };
};

const addCertificateToStyleRow = (
  row: StyleData,
  processTypeId: number,
  poVariantId: number,
  certificate: ProductCertificateDetail
): StyleData => ({
  ...row,
  productCertificateDetailsList: row.productCertificateDetailsList?.map(process => {
    if (process.id !== processTypeId) return process;

    return {
      ...process,
      subsupplierDetails: process.subsupplierDetails?.map(sub => {
        if (Number(sub.poVariantId) !== poVariantId) return sub;

        const existing = sub.certificateDetails ?? [];
        if (
          existing.some(
            item => item.poStyleProductCertificateId === certificate.poStyleProductCertificateId
          )
        ) {
          return sub;
        }

        return {
          ...sub,
          certificateDetails: [...existing, certificate],
        };
      }),
    };
  }),
});

export const applyCertificateAssignResponseToStyle = (
  style: StyleData,
  processTypeId: number,
  response: PoFacilityProcessCertificateResponse
): StyleData => {
  const poVariantId = Number(response.poVariantId);
  if (!poVariantId) return style;

  const certificate = mapPoFacilityCertificateResponseToDetail(response);

  if (style.childs?.length) {
    return {
      ...style,
      childs: style.childs.map(child =>
        Number(child.poVariantId) === poVariantId
          ? addCertificateToStyleRow(child, processTypeId, poVariantId, certificate)
          : child
      ),
    };
  }

  return addCertificateToStyleRow(style, processTypeId, poVariantId, certificate);
};

export const applyCertificateRemoveToStyle = (
  style: StyleData,
  processTypeId: number,
  poFacilityCertificateId: number
): StyleData => {
  const removeFromRow = (row: StyleData): StyleData => ({
    ...row,
    productCertificateDetailsList: row.productCertificateDetailsList?.map(process => {
      if (process.id !== processTypeId) return process;

      return {
        ...process,
        subsupplierDetails: process.subsupplierDetails?.map(sub => ({
          ...sub,
          certificateDetails: (sub.certificateDetails ?? []).filter(
            item => item.poStyleProductCertificateId !== poFacilityCertificateId
          ),
        })),
      };
    }),
  });

  if (style.childs?.length) {
    return {
      ...removeFromRow(style),
      childs: style.childs.map(child =>
        applyCertificateRemoveToStyle(child, processTypeId, poFacilityCertificateId)
      ),
    };
  }

  return removeFromRow(style);
};

export const getMappedFacilityIdsForRow = (row: StyleData, processTypeId: number): string[] => {
  if (isMultiVariantStyleParentRow(row)) {
    return [
      ...new Set(
        (row.childs ?? []).flatMap(child => getMappedFacilityIdsForRow(child, processTypeId))
      ),
    ];
  }

  return getMappedFacilitiesForRow(row, processTypeId).map(facility => String(facility.id));
};

const resolveFacilityCertificateId = (certificate: FacilityCertificate): number => {
  const fromId = Number(certificate.id);
  return Number.isFinite(fromId) && fromId > 0 ? fromId : certificate.standard?.id ?? 0;
};

const getFacilityCertificates = (facility?: FacilityDetail): SubSupplierFacilityCertificate[] =>
  facility?.certificates ?? facility?.certificate ?? [];

const getSubSupplierCatalogKey = (sub: SubsupplierDetail): string => {
  const organizationSubSupplierId = String(sub.organizationSubSupplierId ?? '').trim();
  if (organizationSubSupplierId) return organizationSubSupplierId;

  const id = String(sub.id ?? '').trim();
  // Styles/contents uses FieldData.Id default 0 for direct-owned facility groups (DL-61246).
  if (!id || id === '0') return '';

  return id;
};

const isDirectSubSupplierGroup = (sub: SubsupplierDetail): boolean =>
  !getSubSupplierCatalogKey(sub);

/** Match catalog facility within the same sub-supplier scope as the style row (direct vs named). */
const findCatalogFacilityInProcess = (
  poProcess: PoProcessType | undefined,
  facilityId: string,
  subSupplierId: string
): FacilityDetail | undefined => {
  const normalizedFacilityId = String(facilityId);
  const subs = poProcess?.subsupplierDetails ?? [];

  if (subSupplierId) {
    return subs
      .find(item => getSubSupplierCatalogKey(item) === subSupplierId)
      ?.facilityDetails?.find(item => String(item.id) === normalizedFacilityId);
  }

  return subs
    .filter(isDirectSubSupplierGroup)
    .flatMap(item => item.facilityDetails ?? [])
    .find(item => String(item.id) === normalizedFacilityId);
};

type AssignedFacilityCertificate = SubSupplierFacilityCertificate & {
  poStyleProductCertificateId?: number;
  /** Contents API (`styles/contents?type=certificates`) assignment id. */
  poFacilityProcessCertificateId?: number;
  /** When present on contents API, matches assign payload `facilityCertificateId`. */
  facilityCertificateId?: number;
  status?: string;
  /** Contents API flat document URL (`PoFacilityCertificate.documentUrl`). */
  documentUrl?: string | null;
};

/** Resolve catalog `facilityCertificateId` (assign payload id) from sub-suppliers API options. */
export const lookupCatalogFacilityCertificateId = (
  certificate: AssignedFacilityCertificate,
  facility: FacilityDetail,
  sub: SubsupplierDetail,
  poSubSupplierProcessTypes: PoProcessType[] | undefined,
  processTypeId: number
): number | undefined => {
  const explicitCatalogId = Number(certificate.facilityCertificateId);
  if (Number.isFinite(explicitCatalogId) && explicitCatalogId > 0) {
    return explicitCatalogId;
  }

  const subSupplierId = getSubSupplierCatalogKey(sub);
  const facilityId = String(facility.id ?? '');
  const standardName = certificate.standard?.name?.trim() ?? '';
  const certificateNumber = certificate.certificateNumber?.trim() ?? '';
  if (!facilityId || !standardName || !poSubSupplierProcessTypes?.length) {
    return undefined;
  }

  const poProcess = poSubSupplierProcessTypes.find(process => process.id === processTypeId);
  const poFacility = findCatalogFacilityInProcess(poProcess, facilityId, subSupplierId);

  for (const catalogCert of getFacilityCertificates(poFacility).filter(isProductCertificate)) {
    const catalogStandard = catalogCert.standard?.name?.trim() ?? '';
    const catalogNumber = catalogCert.certificateNumber?.trim() ?? '';
    if (catalogStandard === standardName && catalogNumber === certificateNumber) {
      const catalogId = resolveFacilityCertificateId(catalogCert);
      if (catalogId > 0) return catalogId;
    }
  }

  return undefined;
};

const mapFacilityCertificateToProductCertificateDetail = (
  certificate: AssignedFacilityCertificate,
  facility: FacilityDetail,
  sub: SubsupplierDetail,
  context?: AssignedCertificateCatalogContext
): ProductCertificateDetail | null => {
  if (!isProductCertificate(certificate)) return null;

  const standardName = certificate.standard?.name?.trim() ?? '';
  const certificateNumber = certificate.certificateNumber?.trim() ?? '';
  if (!standardName) return null;

  const assignmentId =
    certificate.poStyleProductCertificateId ??
    certificate.poFacilityProcessCertificateId ??
    (Number.isFinite(Number(certificate.id)) && Number(certificate.id) > 0
      ? Number(certificate.id)
      : undefined);

  const catalogId =
    context?.processTypeId != null
      ? lookupCatalogFacilityCertificateId(
          certificate,
          facility,
          sub,
          context.poSubSupplierProcessTypes,
          context.processTypeId
        )
      : undefined;

  const label = certificateNumber ? `${standardName} · ${certificateNumber}` : standardName;
  const documentUrl =
    certificate.documentUrl?.trim() || certificate.document?.documentUrl?.trim() || undefined;

  // Dropdown option id and assign payload use catalog facilityCertificateId — not assignment id.
  if (catalogId && catalogId > 0) {
    return {
      id: String(catalogId),
      name: label,
      poStyleProductCertificateId: assignmentId,
      certificateNumber,
      standardName,
      validFrom: certificate.validFrom,
      validTo: certificate.validTo,
      status: certificate.status,
      facilityId: String(facility.id),
      documentUrl,
    };
  }

  if (context?.poSubSupplierProcessTypes != null) {
    if (!assignmentId) return null;
    // Assigned on style but not in sub-suppliers catalog — show in DD using assignment id.
    return {
      id: `orphan-${assignmentId}`,
      name: label,
      poStyleProductCertificateId: assignmentId,
      certificateNumber,
      standardName,
      validFrom: certificate.validFrom,
      validTo: certificate.validTo,
      status: certificate.status,
      facilityId: String(facility.id),
      documentUrl,
    };
  }

  const facilityCertificateId = resolveFacilityCertificateId(certificate);
  if (facilityCertificateId <= 0) return null;

  return {
    id: String(facilityCertificateId),
    name: label,
    poStyleProductCertificateId: assignmentId,
    certificateNumber,
    standardName,
    validFrom: certificate.validFrom,
    validTo: certificate.validTo,
    status: certificate.status,
    facilityId: String(facility.id),
    documentUrl,
  };
};

/** Assigned/mapped product certificates from contents API (`certificateDetails` or `facilityDetails.certificates`). */
export const getAssignedProductCertificatesFromSub = (
  sub: SubsupplierDetail,
  context?: AssignedCertificateCatalogContext
): ProductCertificateDetail[] => {
  const fromFacilities = (sub.facilityDetails ?? []).flatMap(facility =>
    getFacilityCertificates(facility)
      .map(certificate =>
        mapFacilityCertificateToProductCertificateDetail(certificate, facility, sub, context)
      )
      .filter((certificate): certificate is ProductCertificateDetail => certificate != null)
  );

  const fromDetails = sub.certificateDetails ?? [];
  if (fromDetails.length === 0) {
    return [...fromFacilities].sort((a, b) => a.name.localeCompare(b.name));
  }

  const merged = new Map<string, ProductCertificateDetail>();
  for (const certificate of fromFacilities) {
    merged.set(certificate.id, certificate);
  }
  for (const certificate of fromDetails) {
    const existing = merged.get(String(certificate.id));
    merged.set(String(certificate.id), {
      ...existing,
      ...certificate,
      facilityId: certificate.facilityId ?? existing?.facilityId,
    });
  }

  return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export const getAssignedProductCertificatesForProcess = (
  processDetail?: PoProcessType,
  context?: AssignedCertificateCatalogContext
): ProductCertificateDetail[] =>
  processDetail?.subsupplierDetails?.flatMap(sub =>
    getAssignedProductCertificatesFromSub(sub, context)
  ) ?? [];

const mapFacilityCertificateToOption = (
  certificate: FacilityCertificate,
  sub: SubsupplierDetail,
  facility: FacilityDetail
): StyleProductCertificateOption | null => {
  if (!isProductCertificate(certificate)) return null;

  const standardName = certificate.standard?.name?.trim() ?? '';
  const certificateNumber = certificate.certificateNumber?.trim() ?? '';
  const facilityCertificateId = resolveFacilityCertificateId(certificate);
  if (!standardName || facilityCertificateId <= 0) return null;

  return {
    id: String(facilityCertificateId),
    facilityCertificateId,
    label: certificateNumber ? `${standardName} · ${certificateNumber}` : standardName,
    certificateNumber,
    standardName,
    instituteName: certificate.institute?.name ?? '',
    facilityId: String(facility.id),
    facilityName: facility.name,
    subsupplierId: sub.organizationSubSupplierId ?? sub.id,
    validFrom: certificate.validFrom,
    validTo: certificate.validTo,
    status: certificate.status,
    supplierStatus: facility.status,
    documentUrl: certificate.document?.documentUrl,
  };
};

const getProductCertificatesOnPoFacility = (
  poSubSupplierProcessTypes: PoProcessType[] | undefined,
  processTypeId: number,
  subSupplierId: string,
  facilityId: string
) => {
  const poProcess = poSubSupplierProcessTypes?.find(process => process.id === processTypeId);
  const poFacility = findCatalogFacilityInProcess(poProcess, facilityId, subSupplierId);
  return getFacilityCertificates(poFacility).filter(isProductCertificate);
};

export const getProductCertificateOptionsFromStyleRow = (
  row: StyleData,
  processTypeId: number,
  poSubSupplierProcessTypes?: PoProcessType[]
): StyleProductCertificateOption[] => {
  if (isMultiVariantStyleParentRow(row)) {
    const optionsById = new Map(
      (row.childs ?? [])
        .flatMap(child =>
          getProductCertificateOptionsFromStyleRow(child, processTypeId, poSubSupplierProcessTypes)
        )
        .map(option => [option.facilityCertificateId, option] as const)
    );
    return sortByLabel([...optionsById.values()]);
  }

  const mappedFacilityIds = new Set(getMappedFacilityIdsForRow(row, processTypeId));
  if (!mappedFacilityIds.size) return [];

  const optionsById = new Map<number, StyleProductCertificateOption>();

  for (const sub of getStyleProcessDetailsForRow(row, processTypeId)?.subsupplierDetails ?? []) {
    const subSupplierId = getSubSupplierCatalogKey(sub);

    for (const facility of sub.facilityDetails ?? []) {
      const facilityId = String(facility.id ?? '');
      if (!mappedFacilityIds.has(facilityId)) continue;

      for (const certificate of getProductCertificatesOnPoFacility(
        poSubSupplierProcessTypes,
        processTypeId,
        subSupplierId,
        facilityId
      )) {
        const option = mapFacilityCertificateToOption(certificate, sub, facility);
        if (option) optionsById.set(option.facilityCertificateId, option);
      }
    }
  }

  return sortByLabel([...optionsById.values()]);
};

export const variantOffersFacilityCertificate = (
  row: StyleData,
  processTypeId: number,
  facilityCertificateId: number,
  poSubSupplierProcessTypes?: PoProcessType[],
  mappedFacilityId?: string
): boolean => {
  if (mappedFacilityId) {
    return styleRowOffersMappedFacilityCertificate(
      row,
      processTypeId,
      mappedFacilityId,
      facilityCertificateId,
      poSubSupplierProcessTypes
    );
  }

  return getProductCertificateOptionsFromStyleRow(
    row,
    processTypeId,
    poSubSupplierProcessTypes
  ).some(option => option.facilityCertificateId === facilityCertificateId);
};

export const styleRowOffersMappedFacilityCertificate = (
  row: StyleData,
  processTypeId: number,
  mappedFacilityId: string,
  facilityCertificateId: number,
  poSubSupplierProcessTypes?: PoProcessType[]
): boolean => {
  if (!getMappedFacilityIdsForRow(row, processTypeId).includes(mappedFacilityId)) {
    return false;
  }

  return getProductCertificateOptionsFromStyleRow(
    row,
    processTypeId,
    poSubSupplierProcessTypes
  ).some(
    option =>
      option.facilityCertificateId === facilityCertificateId &&
      option.facilityId === mappedFacilityId
  );
};

export const parseStyleMapSupplierRowKey = (rowKey: string) => {
  const [poVariantId, styleColorId, styleId] = rowKey.split('-');
  return { poVariantId, styleColorId, styleId };
};

export const resolveVariantRowsForRowKey = (
  rows: StyleData[] | undefined,
  rowKey: string
): StyleData[] => {
  const { poVariantId, styleColorId, styleId } = parseStyleMapSupplierRowKey(rowKey);
  const parent = findStyleRowInMapList(rows, styleId);
  if (!parent) return [];

  if (styleColorId === 'undefined' || styleColorId === '') {
    return parent.childs?.length ? parent.childs : [parent];
  }

  const variant = findVariantStyleRowInMapList(rows, styleId, poVariantId, styleColorId);
  return variant ? [variant] : [];
};

export const expandStyleMapSupplierRowKeys = (rowKeys: string[], rows?: StyleData[]): string[] =>
  [
    ...new Set(
      rowKeys.flatMap(rowKey =>
        resolveVariantRowsForRowKey(rows, rowKey).map(variantRow =>
          getStyleMapSuppliersRowId(variantRow)
        )
      )
    ),
  ].filter(rowKey => {
    const { styleColorId } = parseStyleMapSupplierRowKey(rowKey);
    return styleColorId !== 'undefined' && styleColorId !== '';
  });

export const getProductCertificateOptionsForSelectedRows = (
  rows: StyleData[] | undefined,
  selectedRowIds: string[],
  processTypeId: number,
  poSubSupplierProcessTypes?: PoProcessType[]
): StyleProductCertificateOption[] => {
  const optionsByKey = new Map<string, StyleProductCertificateOption>();

  for (const rowKey of selectedRowIds) {
    for (const row of resolveVariantRowsForRowKey(rows, rowKey)) {
      for (const option of getProductCertificateOptionsFromStyleRow(
        row,
        processTypeId,
        poSubSupplierProcessTypes
      )) {
        optionsByKey.set(`${option.facilityId}-${option.facilityCertificateId}`, option);
      }
    }
  }

  return sortByLabel([...optionsByKey.values()]);
};

export const getStyleDisplayNamesForCertificateAssign = (
  rows: StyleData[] | undefined,
  eligibleRowKeys: string[]
): string[] => {
  const names = new Set<string>();

  for (const rowKey of eligibleRowKeys) {
    const { styleId } = parseStyleMapSupplierRowKey(rowKey);
    const parent = findStyleRowInMapList(rows, styleId);
    const name = parent?.styleName?.trim() || parent?.styleNumber?.trim();
    if (name) names.add(name);
  }

  return [...names].sort((a, b) => a.localeCompare(b));
};

export const filterEligibleCertificateAssignRowKeys = ({
  rowKeys,
  allRows,
  processTypeId,
  facilityCertificateId,
  mappedFacilityId,
  poSubSupplierProcessTypes,
  isCertificateAssigned,
}: {
  rowKeys: string[];
  allRows: StyleData[] | undefined;
  processTypeId: number;
  facilityCertificateId: number;
  mappedFacilityId?: string;
  poSubSupplierProcessTypes?: PoProcessType[];
  isCertificateAssigned: (poVariantId: string, styleId: string, styleColorId: string) => boolean;
}): string[] => {
  const variantRowKeys = expandStyleMapSupplierRowKeys(rowKeys, allRows);

  return variantRowKeys.filter(rowKey => {
    const { poVariantId, styleColorId, styleId } = parseStyleMapSupplierRowKey(rowKey);
    const styleRow = findVariantStyleRowInMapList(allRows, styleId, poVariantId, styleColorId);
    if (!styleRow) return false;

    return (
      !isCertificateAssigned(poVariantId, styleId, styleColorId) &&
      variantOffersFacilityCertificate(
        styleRow,
        processTypeId,
        facilityCertificateId,
        poSubSupplierProcessTypes,
        mappedFacilityId
      )
    );
  });
};

const mapCertificateDetailsToValue = (
  sub: SubsupplierDetail,
  certificates: ProductCertificateDetail[] = []
): CertificateInfoExtract[] =>
  certificates.map(certificate => {
    const isOrphanId = String(certificate.id).startsWith('orphan-');
    const catalogFacilityCertificateId = Number(certificate.id);
    const hasCatalogId =
      !isOrphanId &&
      Number.isFinite(catalogFacilityCertificateId) &&
      catalogFacilityCertificateId > 0;
    const facilityStatus = sub.facilityDetails?.find(
      facility => String(facility.id) === String(certificate.facilityId ?? '')
    )?.status;

    return {
      id: String(certificate.id),
      label: certificate.name,
      supplierName: sub.name,
      supplierId: sub.organizationSubSupplierId ?? sub.id,
      supplierAvatarColor: sub.avatarColor,
      customProps: {
        id: String(certificate.id),
        label: certificate.name,
        standardName: certificate.standardName ?? certificate.name.split(' · ')[0]?.trim(),
        certificateNumber: certificate.certificateNumber,
        validFrom: certificate.validFrom,
        validTo: certificate.validTo,
        status: certificate.status,
        supplierStatus: facilityStatus,
        uiCapabilities: sub.uiCapabilities,
        supplierId: sub.organizationSubSupplierId ?? sub.id,
        supplierName: sub.name,
        supplierAvatarColor: sub.avatarColor,
        poStyleProductCertificateId: certificate.poStyleProductCertificateId,
        facilityCertificateId: hasCatalogId ? catalogFacilityCertificateId : undefined,
        facilityId: certificate.facilityId,
        documentUrl: certificate.documentUrl,
      },
    };
  });

export const extractCertificateInfo = (
  data?: PoProcessType,
  context?: AssignedCertificateCatalogContext
): CertificateInfoExtract[] =>
  data?.subsupplierDetails?.flatMap(sub =>
    mapCertificateDetailsToValue(sub, getAssignedProductCertificatesFromSub(sub, context))
  ) ?? [];

/** Product certificate type id in `facilitycertificate` query param (`{certId}_1`). */
export const PRODUCT_CERTIFICATE_FILTER_TYPE = 1;

export const formatProductCertificateFilterLabel = (
  standardName: string,
  certificateNumber?: string
) => {
  const number = certificateNumber?.trim();
  return number ? `${standardName} (${number})` : standardName;
};

export const extractCatalogCertificateFilterOptions = (data?: PoProcessType): ComboBoxOption[] => {
  const optionsByCertificateId = new Map<number, ComboBoxOption>();

  for (const sub of data?.subsupplierDetails ?? []) {
    for (const facility of sub.facilityDetails ?? []) {
      for (const certificate of getFacilityCertificates(facility).filter(isProductCertificate)) {
        const standardName = certificate.standard?.name?.trim() ?? '';
        const certificateNumber = certificate.certificateNumber?.trim() ?? '';
        const facilityCertificateId = resolveFacilityCertificateId(certificate);
        if (!standardName || facilityCertificateId <= 0) continue;
        if (optionsByCertificateId.has(facilityCertificateId)) continue;

        optionsByCertificateId.set(facilityCertificateId, {
          id: String(facilityCertificateId),
          label: formatProductCertificateFilterLabel(standardName, certificateNumber),
          customProps: {
            facilityName: facility.name,
            facilityCertificateId,
            certificateNumber,
            standardName,
          },
          CustomElement: ProductCertificateFilterOptionElement,
        });
      }
    }
  }

  return sortByLabel([...optionsByCertificateId.values()]);
};

export const getFacilityProcessCertificateFilterKey = (processTypeId: number) =>
  `facilityprocesscertificate_${processTypeId}`;

/** Maps per-process-type filter selections to BE `facilityprocesscertificate_{processTypeId}` params. */
export const buildFacilityProcessCertificateFilterParams = (
  state: Record<string, unknown> | undefined,
  processTypes?: PoProcessType[]
): Record<string, string> => {
  const params: Record<string, string> = {};

  for (const processType of processTypes ?? []) {
    const key = getFacilityProcessCertificateFilterKey(processType.id);
    const selected = state?.[key];
    if (selected == null || selected === '') continue;

    const certificateIds = [
      ...new Set(
        String(selected)
          .split(',')
          .map(rawId => {
            const value = String(rawId).trim();
            const plainId = value.includes('_') ? value.split('_')[0] : value;
            return Number(plainId);
          })
          .filter(id => Number.isFinite(id) && id > 0)
      ),
    ];

    if (!certificateIds.length) continue;

    // BE expects plain FacilityCertificate ids: facilityprocesscertificate_45=101,102
    params[key] = certificateIds.map(String).join(',');
  }

  return params;
};

export const formatPoProcessTypesToCertificateFilter = (
  processTypes?: PoProcessType[]
): BaseColumns =>
  processTypes?.reduce<BaseColumns>(
    (prev, curr) => ({
      ...prev,
      [getFacilityProcessCertificateFilterKey(curr?.id)]: {
        id: getFacilityProcessCertificateFilterKey(curr?.id),
        label: curr?.name,
        type: 'dd',
        options: extractCatalogCertificateFilterOptions(curr),
      },
    }),
    {}
  ) ?? {};

const mapStyleProductCertificateOptionToComboBox = (
  option: StyleProductCertificateOption
): ComboBoxOption => ({
  id: option.id,
  label: `${option.standardName} ${option.certificateNumber} ${option.facilityName}`.trim(),
  customProps: {
    id: option.id,
    label: option.label,
    supplierId: option.subsupplierId,
    certificateNumber: option.certificateNumber,
    standardName: option.standardName,
    validFrom: option.validFrom,
    validTo: option.validTo,
    status: option.status,
    supplierStatus: option.supplierStatus,
    facilityCertificateId: option.facilityCertificateId,
    facilityId: option.facilityId,
    facilityName: option.facilityName,
    documentUrl: option.documentUrl,
    searchText: `${option.facilityName} ${option.standardName} ${option.certificateNumber}`,
  },
});

export const mapProductCertificateOptionsToComboBox = (
  options: StyleProductCertificateOption[]
): ComboBoxOption[] => {
  const byFacility = options.reduce<Map<string, StyleProductCertificateOption[]>>((acc, option) => {
    const key = option.facilityId || option.facilityName;
    acc.set(key, [...(acc.get(key) ?? []), option]);
    return acc;
  }, new Map());

  return [...byFacility.values()]
    .sort((a, b) => (a[0]?.facilityName ?? '').localeCompare(b[0]?.facilityName ?? ''))
    .flatMap(facilityOptions => {
      const { facilityId = '', facilityName = '' } = facilityOptions[0] ?? {};
      const searchText = facilityOptions
        .map(item => `${item.standardName} ${item.certificateNumber}`)
        .join(' ');

      return [
        {
          id: `${PRODUCT_CERTIFICATE_GROUP_HEADER_PREFIX}${facilityId}`,
          label: facilityName,
          customProps: {
            isGroupHeader: true,
            facilityId,
            facilityName,
            searchText: `${facilityName} ${searchText}`,
          },
        },
        ...sortByLabel(facilityOptions).map(mapStyleProductCertificateOptionToComboBox),
      ];
    });
};
